<template>
    <Head :title="`Investigation: ${incident.incident_number}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!--Header -->
        <div class="flex items-center justify-between mb-6">
            <div class="flex items-center space-x-4">
                <Link
                    :href="route('incidents.show', incident.id)"
                    class="text-gray-500 hover:text-gray-700"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                </Link>
                <div>
                    <h2 class="text-2xl font-semibold text-gray-900">
                        Investigation: {{ incident.incident_number }}
                    </h2>
                    <p class="text-sm text-gray-600 mt-1">{{ incident.title }}</p>
                </div>
            </div>

            <div class="flex items-center space-x-2">
                <button
                    v-if="investigation.status !== 'completed'"
                    @click="saveInvestigation"
                    :disabled="saving"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 disabled:opacity-50"
                >
                    {{ saving ? 'Saving...' : 'Save Progress' }}
                </button>
                <button
                    v-if="investigation.status !== 'completed'"
                    @click="showCompleteModal = true"
                    class="inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700"
                >
                    Complete Investigation
                </button>
                <button
                    @click="generateReport"
                    class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    Generate Report
                </button>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Main Content -->
            <div class="lg:col-span-2 space-y-6">
                <!-- Investigation Overview -->
                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Investigation Overview</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                v-model="form.status"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :disabled="investigation.status === 'completed'"
                            >
                                <option value="not_started">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="on_hold">On Hold</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Lead Investigator</label>
                            <p class="text-gray-900 pt-2">{{ investigation.lead_investigator?.name || 'N/A' }}</p>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Started At</label>
                            <p class="text-gray-900 pt-2">{{ formatDate(investigation.started_at) }}</p>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Estimated Completion</label>
                            <input
                                v-model="form.estimated_completion_date"
                                type="date"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :disabled="investigation.status === 'completed'"
                            />
                        </div>
                    </div>

                    <!-- Progress Bar -->
                    <div class="mt-6">
                        <div class="flex items-center justify-between mb-2">
                            <label class="text-sm font-medium text-gray-700">Overall Progress</label>
                            <span class="text-sm text-gray-600">{{ completionPercentage }}%</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                                class="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                :style="{ width: completionPercentage + '%' }"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Investigation Checklist -->
                <div class="bg-white rounded-lg shadow p-6">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-semibold text-gray-900">Investigation Checklist</h3>
                        <button
                            v-if="investigation.status !== 'completed'"
                            @click="showAddChecklistItemModal = true"
                            class="text-sm text-blue-600 hover:text-blue-800"
                        >
                            + Add Item
                        </button>
                    </div>

                    <div v-if="checklist.length > 0" class="space-y-3">
                        <div
                            v-for="(item, index) in checklist"
                            :key="index"
                            class="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50"
                        >
                            <input
                                v-model="item.completed"
                                type="checkbox"
                                class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                :disabled="investigation.status === 'completed'"
                            />
                            <div class="flex-1">
                                <p
                                    :class="{ 'line-through text-gray-500': item.completed }"
                                    class="text-sm text-gray-900"
                                >
                                    {{ item.title }}
                                </p>
                                <p v-if="item.description" class="text-xs text-gray-500 mt-1">
                                    {{ item.description }}
                                </p>
                            </div>
                            <button
                                v-if="item.custom && investigation.status !== 'completed'"
                                @click="removeChecklistItem(index)"
                                class="text-red-600 hover:text-red-800"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div v-else class="text-center py-8 text-gray-500">
                        <p>No checklist items. Add items to track your investigation progress.</p>
                    </div>
                </div>

                <!-- Root Cause Analysis -->
                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Root Cause Analysis</h3>

                    <div class="space-y-6">
                        <!-- Methodology -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Investigation Methodology
                            </label>
                            <select
                                v-model="form.methodology_used"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :disabled="investigation.status === 'completed'"
                            >
                                <option value="">Select Methodology</option>
                                <option value="5 Whys">5 Whys</option>
                                <option value="Fishbone Diagram">Fishbone Diagram (Ishikawa)</option>
                                <option value="Timeline Analysis">Timeline Analysis</option>
                                <option value="Fault Tree Analysis">Fault Tree Analysis</option>
                                <option value="Change Analysis">Change Analysis</option>
                                <option value="Barrier Analysis">Barrier Analysis</option>
                                <option value="Other">Other</option>
                            </select>
                            <p v-if="form.methodology_used" class="mt-2 text-sm text-gray-600">
                                {{ methodologyDescriptions[form.methodology_used] }}
                            </p>
                        </div>

                        <!-- Primary Root Cause -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Primary Root Cause
                            </label>
                            <textarea
                                v-model="form.root_cause_primary"
                                rows="4"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Identify the primary root cause of the incident..."
                                :disabled="investigation.status === 'completed'"
                            ></textarea>
                        </div>

                        <!-- Contributing Factors -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Contributing Factors
                            </label>
                            <div class="space-y-2">
                                <div
                                    v-for="(factor, index) in contributingFactors"
                                    :key="index"
                                    class="flex items-start space-x-2"
                                >
                                    <span class="text-gray-400 mt-2">•</span>
                                    <input
                                        v-model="contributingFactors[index]"
                                        type="text"
                                        class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        placeholder="Contributing factor..."
                                        :disabled="investigation.status === 'completed'"
                                    />
                                    <button
                                        v-if="investigation.status !== 'completed'"
                                        @click="removeContributingFactor(index)"
                                        class="text-red-600 hover:text-red-800 mt-2"
                                    >
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>
                                <button
                                    v-if="investigation.status !== 'completed'"
                                    @click="addContributingFactor"
                                    class="text-sm text-blue-600 hover:text-blue-800"
                                >
                                    + Add Contributing Factor
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Findings -->
                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Investigation Findings</h3>

                    <div class="space-y-6">
                        <!-- Investigation Summary -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Investigation Summary
                            </label>
                            <textarea
                                v-model="form.investigation_summary"
                                rows="6"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Provide a comprehensive summary of the investigation..."
                                :disabled="investigation.status === 'completed'"
                            ></textarea>
                        </div>

                        <!-- Key Findings -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Key Findings
                            </label>
                            <textarea
                                v-model="form.findings"
                                rows="6"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Document the key findings from your investigation..."
                                :disabled="investigation.status === 'completed'"
                            ></textarea>
                        </div>

                        <!-- Recommendations -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Recommendations
                            </label>
                            <textarea
                                v-model="form.recommendations"
                                rows="6"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Provide recommendations to prevent similar incidents..."
                                :disabled="investigation.status === 'completed'"
                            ></textarea>
                        </div>

                        <!-- Lessons Learned -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Lessons Learned
                            </label>
                            <textarea
                                v-model="form.lessons_learned"
                                rows="6"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Document lessons learned and knowledge gained from this investigation..."
                                :disabled="investigation.status === 'completed'"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sidebar -->
            <div class="lg:col-span-1">
                <!-- Evidence Collection -->
                <div class="bg-white rounded-lg shadow p-6 mb-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Evidence & Witnesses</h3>

                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Evidence Collected
                            </label>
                            <input
                                v-model.number="form.evidence_collected"
                                type="number"
                                min="0"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :disabled="investigation.status === 'completed'"
                            />
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Witnesses Interviewed
                            </label>
                            <input
                                v-model.number="form.witnesses_interviewed"
                                type="number"
                                min="0"
                                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :disabled="investigation.status === 'completed'"
                            />
                        </div>
                    </div>
                </div>

                <!-- Quick Stats -->
                <div class="bg-white rounded-lg shadow p-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Investigation Stats</h3>
                    <dl class="space-y-4">
                        <div>
                            <dt class="text-sm font-medium text-gray-500">Duration</dt>
                            <dd class="mt-1 text-sm text-gray-900">{{ investigationDuration }}</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500">Checklist Progress</dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                {{ completedChecklistItems }}/{{ checklist.length }} items
                            </dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500">Evidence Collected</dt>
                            <dd class="mt-1 text-sm text-gray-900">{{ form.evidence_collected }} items</dd>
                        </div>
                        <div>
                            <dt class="text-sm font-medium text-gray-500">Witnesses</dt>
                            <dd class="mt-1 text-sm text-gray-900">{{ form.witnesses_interviewed }} interviewed</dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        <!-- Modals -->
        <!-- Add Checklist Item Modal -->
        <div v-if="showAddChecklistItemModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showAddChecklistItemModal = false"></div>
                <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white p-6">
                        <h3 class="text-lg font-semibold text-gray-900 mb-4">Add Checklist Item</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input
                                    v-model="newChecklistItem.title"
                                    type="text"
                                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="e.g., Interview affected users"
                                />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    v-model="newChecklistItem.description"
                                    rows="3"
                                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="Additional details..."
                                ></textarea>
                            </div>
                        </div>
                        <div class="flex justify-end space-x-3 mt-6">
                            <button
                                @click="showAddChecklistItemModal = false"
                                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                @click="addChecklistItem"
                                class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                            >
                                Add Item
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Complete Investigation Modal -->
        <div v-if="showCompleteModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showCompleteModal = false"></div>
                <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white p-6">
                        <h3 class="text-lg font-semibold text-gray-900 mb-4">Complete Investigation</h3>
                        <p class="text-sm text-gray-600 mb-4">
                            Are you sure you want to mark this investigation as completed? This will finalize all findings and recommendations.
                        </p>
                        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <p class="text-sm text-yellow-800">
                                <strong>Note:</strong> Once completed, you will not be able to edit the investigation details. Make sure all findings and recommendations are documented.
                            </p>
                        </div>
                        <div class="flex justify-end space-x-3">
                            <button
                                @click="showCompleteModal = false"
                                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                @click="completeInvestigation"
                                class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                            >
                                Complete Investigation
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, Link, Head } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
    incident: {
        type: Object,
        required: true,
    },
    investigation: {
        type: Object,
        required: true,
    },
});

// Form data
const form = ref({
    status: props.investigation.status || 'in_progress',
    estimated_completion_date: props.investigation.estimated_completion_date || '',
    methodology_used: props.investigation.methodology_used || '',
    root_cause_primary: props.investigation.root_cause_primary || '',
    root_cause_contributing: props.investigation.root_cause_contributing || '',
    investigation_summary: props.investigation.investigation_summary || '',
    findings: props.investigation.findings || '',
    recommendations: props.investigation.recommendations || '',
    lessons_learned: props.investigation.lessons_learned || '',
    evidence_collected: props.investigation.evidence_collected || 0,
    witnesses_interviewed: props.investigation.witnesses_interviewed || 0,
});

// Checklist
const checklist = ref([
    { title: 'Review incident timeline', description: 'Document exact sequence of events', completed: false, custom: false },
    { title: 'Interview affected users', description: 'Gather firsthand accounts', completed: false, custom: false },
    { title: 'Collect system logs', description: 'Gather relevant technical data', completed: false, custom: false },
    { title: 'Identify root cause', description: 'Determine primary cause of incident', completed: false, custom: false },
    { title: 'Document findings', description: 'Complete investigation summary', completed: false, custom: false },
]);

// Contributing factors
const contributingFactors = ref(
    props.investigation.root_cause_contributing
        ? props.investigation.root_cause_contributing.split('\n').filter(f => f.trim())
        : ['']
);

// Modals
const showAddChecklistItemModal = ref(false);
const showCompleteModal = ref(false);

// New checklist item
const newChecklistItem = ref({
    title: '',
    description: '',
});

// State
const saving = ref(false);

// Methodology descriptions
const methodologyDescriptions = {
    '5 Whys': 'Ask "why" five times to drill down to the root cause. Simple and effective for straightforward incidents.',
    'Fishbone Diagram': 'Ishikawa diagram helps identify multiple potential causes organized by category (people, process, equipment, environment).',
    'Timeline Analysis': 'Create a detailed timeline of events to understand the sequence and identify where things went wrong.',
    'Fault Tree Analysis': 'Top-down approach starting with the incident and working backwards to identify contributing failures.',
    'Change Analysis': 'Compare what should have happened vs. what actually happened to identify deviations.',
    'Barrier Analysis': 'Identify what barriers failed to prevent the incident and why.',
    'Other': 'Custom investigation methodology.',
};

// Computed properties
const completionPercentage = computed(() => {
    let score = 0;
    let total = 100;

    // Checklist completion (30%)
    if (checklist.value.length > 0) {
        const checklistScore = (completedChecklistItems.value / checklist.value.length) * 30;
        score += checklistScore;
    }

    // Root cause analysis (20%)
    if (form.value.root_cause_primary) score += 10;
    if (form.value.methodology_used) score += 10;

    // Findings (30%)
    if (form.value.investigation_summary) score += 10;
    if (form.value.findings) score += 10;
    if (form.value.recommendations) score += 10;

    // Evidence & interviews (20%)
    if (form.value.evidence_collected > 0) score += 10;
    if (form.value.witnesses_interviewed > 0) score += 10;

    return Math.round(score);
});

const completedChecklistItems = computed(() => {
    return checklist.value.filter(item => item.completed).length;
});

const investigationDuration = computed(() => {
    if (!props.investigation.started_at) return 'Not started';

    const start = new Date(props.investigation.started_at);
    const end = props.investigation.completed_at ? new Date(props.investigation.completed_at) : new Date();

    const diffMs = end - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''}, ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
});

// Methods
const addChecklistItem = () => {
    if (!newChecklistItem.value.title) {
        alert('Please enter a title for the checklist item');
        return;
    }

    checklist.value.push({
        title: newChecklistItem.value.title,
        description: newChecklistItem.value.description,
        completed: false,
        custom: true,
    });

    newChecklistItem.value = { title: '', description: '' };
    showAddChecklistItemModal.value = false;
};

const removeChecklistItem = (index) => {
    checklist.value.splice(index, 1);
};

const addContributingFactor = () => {
    contributingFactors.value.push('');
};

const removeContributingFactor = (index) => {
    contributingFactors.value.splice(index, 1);
};

const saveInvestigation = () => {
    saving.value = true;

    // Prepare data
    const data = {
        ...form.value,
        root_cause_contributing: contributingFactors.value
            .filter(f => f.trim())
            .join('\n'),
    };

    router.post(route('incidents.investigation.update', props.investigation.id), data, {
        preserveScroll: true,
        onFinish: () => {
            saving.value = false;
        },
    });
};

const completeInvestigation = () => {
    form.value.status = 'completed';

    router.post(route('incidents.investigation.update', props.investigation.id), {
        ...form.value,
        root_cause_contributing: contributingFactors.value
            .filter(f => f.trim())
            .join('\n'),
        completed_at: new Date().toISOString(),
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showCompleteModal.value = false;
        },
    });
};

const generateReport = () => {
    window.location.href = route('incidents.download-report', props.incident.id);
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};
</script>
